import { useEffect, useMemo, useRef, useState } from 'react';
import { LockOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { designTokens } from '../../../design-system';
import { ActiveStepPanel } from '../components/ActiveStepPanel';
import { SetupTaskButton } from '../components/SetupTaskButton';
import {
  emptyDraft,
  hireDateError,
  InitialAdminSetupFailure,
  inputSteps,
  legalFullName,
  normalizeInitialAdminSetupInput,
  setupTasks,
  taskOrder,
  validName,
  type InitialAdminSetupInput,
  type InitialAdminSetupPort,
  type InitialAdminSetupSession,
  type InputStepId,
  type SetupTask,
  type StepId,
} from '../model/setup';

export type InitialAdminSetupPageProps<TSession> = {
  setup: InitialAdminSetupSession;
  port: InitialAdminSetupPort<TSession>;
  onComplete: (session: TSession) => void;
};

export function InitialAdminSetupPage<TSession>({
  setup,
  port,
  onComplete,
}: InitialAdminSetupPageProps<TSession>) {
  const [activeStep, setActiveStep] = useState<StepId>('account');
  const [draft, setDraft] = useState<InitialAdminSetupInput>(emptyDraft);
  const [confirmedSteps, setConfirmedSteps] = useState<Set<InputStepId>>(() => new Set());
  const [attemptedSteps, setAttemptedSteps] = useState<Set<InputStepId>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [loginRecoveryRequired, setLoginRecoveryRequired] = useState(false);
  const panelHeadingRef = useRef<HTMLHeadingElement>(null);
  const today = useMemo(() => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()), []);

  const isValid = (step: InputStepId) => {
    if (step === 'company') return validName(draft.companyName);
    if (step === 'organization') return validName(draft.rootOrganizationName);
    if (step === 'location') return validName(draft.locationName);
    return validName(draft.legalFamilyName, 100) && validName(draft.legalGivenName, 100) &&
      hireDateError(draft.hireDate, today) === undefined;
  };
  const isComplete = (step: InputStepId) => confirmedSteps.has(step) && isValid(step);
  const allComplete = inputSteps.every(isComplete);

  const isUnlocked = (step: StepId) => {
    if (step === 'account' || step === 'company') return true;
    if (step === 'review') return allComplete;
    if (isComplete(step)) return true;
    const previous = inputSteps[inputSteps.indexOf(step) - 1];
    return previous ? isComplete(previous) : true;
  };

  useEffect(() => {
    panelHeadingRef.current?.focus();
  }, [activeStep]);

  const selectStep = (step: StepId) => {
    if (saving || !isUnlocked(step)) return;
    setActiveStep(step);
    setError(undefined);
    setLoginRecoveryRequired(false);
  };

  const updateField = (step: InputStepId, field: keyof InitialAdminSetupInput, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setConfirmedSteps((current) => {
      const next = new Set(current);
      const changedIndex = inputSteps.indexOf(step);
      inputSteps.slice(changedIndex).forEach((candidate) => next.delete(candidate));
      return next;
    });
  };

  const confirmStep = (step: InputStepId) => {
    setAttemptedSteps((current) => new Set(current).add(step));
    if (!isValid(step)) return;
    setConfirmedSteps((current) => new Set(current).add(step));
    setActiveStep(taskOrder[taskOrder.indexOf(step) + 1]);
  };

  const save = () => {
    if (!allComplete || saving) return;
    setSaving(true);
    setError(undefined);
    setLoginRecoveryRequired(false);
    void port.complete(normalizeInitialAdminSetupInput(draft), setup.csrfToken).then(onComplete, (reason: unknown) => {
      if (reason instanceof InitialAdminSetupFailure && reason.reason === 'already-completed') {
        setError('다른 사용자가 이미 초기 설정을 완료했습니다. 다시 로그인해 현재 조직에 접속해 주세요.');
        setLoginRecoveryRequired(true);
      } else if (reason instanceof InitialAdminSetupFailure && reason.reason === 'expired') {
        setError('초기 설정 세션이 만료되었습니다. Google 계정으로 다시 로그인해 주세요.');
        setLoginRecoveryRequired(true);
      } else {
        setError(reason instanceof Error ? reason.message : '조직과 관리자 정보를 생성하지 못했습니다.');
      }
      setSaving(false);
    });
  };

  const taskDetail = (task: SetupTask) => {
    if (task.id === 'account') return setup.email;
    if (task.id === 'company' && isComplete(task.id)) return draft.companyName.trim();
    if (task.id === 'organization' && isComplete(task.id)) return draft.rootOrganizationName.trim();
    if (task.id === 'location' && isComplete(task.id)) return draft.locationName.trim();
    if (task.id === 'person' && isComplete(task.id)) return `${legalFullName(draft)} · ${draft.hireDate}`;
    if (task.id === 'review' && allComplete) return '모든 입력 완료';
    return task.defaultDetail;
  };

  const completedInputCount = inputSteps.filter(isComplete).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: designTokens.color.canvas }}>
      <Box component="header" sx={{ height: designTokens.layout.workspaceHeaderHeight, borderBottom: `1px solid ${designTokens.color.divider}` }}>
        <Stack direction="row" sx={{ maxWidth: designTokens.layout.pageContentStandardWidth, height: '100%', mx: 'auto', px: { xs: 2, sm: 3 }, alignItems: 'center' }}>
          <Box component="img" src="/heyday-lockup.svg" alt="Heyday" sx={{ width: 104, height: 'auto', display: 'block' }} />
          <Box sx={{ width: '1px', height: 16, mx: 1.5, bgcolor: designTokens.color.divider }} />
          <Typography variant="body2" color="text.secondary">초기 설정</Typography>
        </Stack>
      </Box>

      <Box component="main" sx={{ maxWidth: designTokens.layout.pageContentStandardWidth, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 5 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'flex-end' }, justifyContent: 'space-between', mb: { xs: 3, md: 4 } }}>
          <Box>
            <Typography component="h1" variant="h4">초기 설정</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{completedInputCount + 1} / 5 완료</Typography>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '264px minmax(0, 1fr)' }, borderTop: `1px solid ${designTokens.color.strongDivider}`, borderBottom: `1px solid ${designTokens.color.strongDivider}` }}>
          <Box component="aside" sx={{ bgcolor: designTokens.color.navigationSurface, borderRight: { md: `1px solid ${designTokens.color.divider}` }, borderBottom: { xs: `1px solid ${designTokens.color.divider}`, md: 0 } }}>
            <Box component="nav" aria-label="초기 설정 할 일">
              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {setupTasks.map((task, index) => {
                  const complete = task.id === 'account' || (task.id !== 'review' && isComplete(task.id));
                  const unlocked = isUnlocked(task.id);
                  const active = task.id === activeStep;
                  return (
                    <Box component="li" key={task.id}>
                      <SetupTaskButton task={task} index={index} active={active} complete={complete} locked={!unlocked} detail={taskDetail(task)} disabled={saving || !unlocked} onClick={() => selectStep(task.id)} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0, px: { xs: 0, md: 5 }, py: { xs: 3, md: 4.5 } }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 3 }} action={loginRecoveryRequired ? <Button color="inherit" onClick={() => window.location.assign('/api/auth/login')}>다시 로그인</Button> : undefined}>
                {error}
              </Alert>
            ) : null}
            <ActiveStepPanel activeStep={activeStep} draft={draft} setup={setup} attemptedSteps={attemptedSteps} today={today} saving={saving} allComplete={allComplete} headingRef={panelHeadingRef} onSelectStep={selectStep} onUpdateField={updateField} onConfirmStep={confirmStep} onSave={save} />
          </Box>
        </Box>

        <Stack direction="row" spacing={0.75} sx={{ mt: 2, color: designTokens.color.mutedText, alignItems: 'center' }}>
          <LockOutlined sx={{ fontSize: 15 }} />
          <Typography variant="caption">설정이 끝나기 전에는 일반 Heyday 데이터에 접근할 수 없습니다.</Typography>
        </Stack>
      </Box>
    </Box>
  );
}
